import type {
  CaseReducerActions,
  Dispatch,
  UnknownAction,
} from "@reduxjs/toolkit";

type Dispatchers<T extends CaseReducerActions<any, any>> = {
  [key in keyof T]: T[key] extends (...params: any[]) => any
    ? (...params: Parameters<T[key]>) => void
    : () => void;
};

export function createDispatchers<T extends CaseReducerActions<any, any>>(
  actions: T,
) {
  return function (dispatch: Dispatch<UnknownAction>): Dispatchers<T> {
    return Object.keys(actions).reduce(
      (dispatchers: Dispatchers<T>, key: keyof T): Dispatchers<T> => {
        dispatchers[key] = ((...params: unknown[]) => {
          const actionCreator = actions[key] as (
            ...args: unknown[]
          ) => UnknownAction;
          return dispatch(actionCreator(...(params as never[])));
        }) as Dispatchers<T>[keyof T];

        return dispatchers;
      },
      {} as Dispatchers<T>,
    );
  };
}

/**
 * Creates pre-dispatched action functions that can be called directly
 * without needing to pass dispatch each time.
 *
 * @param actions - Redux Toolkit slice actions
 * @returns Object with functions that automatically dispatch actions
 *
 * @example
 * const { openSuccessSnackBar, openErrorSnackBar } = createDispatcher(snackBarSlice.actions)
 *
 * // Usage - no need to pass dispatch or call useAppDispatch
 * openSuccessSnackBar({ message: 'Success!' })
 * openErrorSnackBar({ message: 'Error occurred', errorId: '123' })
 */
